import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { takeUntil } from 'rxjs';
import { CONTATTI } from '../../../constants/contact.constants';
import {
  LOGO_CONSIGLIO_BASE64,
  LOGO_REGIONE_BASE64,
} from '../../../constants/footer-logo-fallback.constants';
import { BaseComponent } from '../../base.component';
import { ContenutoStaticoService } from '../../../../core/services/contenuto-statico.service';
import {
  CONTENUTI_DINAMICI_KEYS,
  FooterEnteItem,
  FooterLinkItem,
  immagine,
  link,
  testo,
} from '../../../../core/models/contenuto-dinamico.models';
import { ContenutoValorePipe, ContenutoRisorsaPipe } from '../../../pipes/contenuto.pipe';
import { ROUTES } from '../../../constants/routes.constants';

// Un link del footer, risolto per il rendering: ogni `url` arriva sempre come
// URL assoluto (http/s), per non creare ambiguità su cosa sia interno o
// esterno. Il codice decide da sé confrontando l'origin dell'URL con quello
// corrente: se coincide è una pagina interna (navigazione via router, senza
// ricaricare la pagina), altrimenti è un sito esterno (nuova scheda).
interface ResolvedFooterLink {
  label: string;
  url: string;
  external: boolean;
  // Per i link interni: solo pathname/search/hash, usato con routerLink.
  path: string;
}

function resolveLinks(links: FooterLinkItem[]): ResolvedFooterLink[] {
  return links.map((item) => {
    const label = item.label.valore;
    const url = item.url.valore;
    if (url === '#') {
      // Placeholder in attesa di configurazione: non naviga da nessuna parte.
      return { label, url, external: true, path: '#' };
    }
    try {
      const parsed = new URL(url, window.location.origin);
      const external = parsed.origin !== window.location.origin;
      return {
        label,
        url,
        external,
        path: external ? url : parsed.pathname + parsed.search + parsed.hash,
      };
    } catch {
      return { label, url, external: true, path: url };
    }
  });
}

@Component({
  selector: 'app-footer',
  imports: [CommonModule, RouterModule, FormsModule, ContenutoValorePipe, ContenutoRisorsaPipe],
  templateUrl: './footer.component.html',
  styleUrls: ['./footer.component.scss'],
})
export class FooterComponent extends BaseComponent {
  private contenutoStaticoService = inject(ContenutoStaticoService);

  // Fallback: usato finché il contenuto statico "FOOTER_LINKS" non è caricato
  // (o in caso di errore). L'elenco effettivo è dinamico: voci aggiunte o
  // rimosse nel contenuto statico lato BO si riflettono qui senza modifiche al codice.
  links: ResolvedFooterLink[] = resolveLinks([
    {
      label: testo('Linee guida per la digitalizzazione'),
      url: link(window.location.origin + '/' + ROUTES.PROGETTO_LINEE_GUIDA),
    },
    {
      label: testo('Cookie policy'),
      url: link(window.location.origin + '/' + ROUTES.COOKIE_POLICY),
    },
    {
      label: testo('Dichiarazione di accessibilità'),
      url: link('https://form.agid.gov.it/view/67fd65a0-9a26-11f0-ad34-bb702e31756e'),
    },
  ]);

  // Fallback: usato finché il contenuto statico "FOOTER_ENTI" non è caricato
  // (o in caso di errore). Loghi, indirizzi ed email sono gestibili da BO.
  enti: FooterEnteItem[] = [
    {
      nome: testo(CONTATTI.CONSIGLIO.NOME),
      logo: immagine(LOGO_CONSIGLIO_BASE64, 'image/png', 'logoconsiglio.png'),
      website: link(CONTATTI.CONSIGLIO.WEBSITE),
      indirizzo: testo(CONTATTI.CONSIGLIO.INDIRIZZO),
      email: testo(CONTATTI.CONSIGLIO.EMAIL),
    },
    {
      nome: testo(CONTATTI.REGIONE.NOME),
      logo: immagine(LOGO_REGIONE_BASE64, 'image/png', 'logoregione.png'),
      website: link(CONTATTI.REGIONE.WEBSITE),
      indirizzo: testo(CONTATTI.REGIONE.INDIRIZZO),
      email: testo(CONTATTI.REGIONE.EMAIL),
    },
  ];

  override ngOnInit(): void {
    super.ngOnInit();
    this.contenutoStaticoService
      .getByKey(CONTENUTI_DINAMICI_KEYS.FOOTER_LINKS)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (contenuto) => {
          if (contenuto.links?.length) this.links = resolveLinks(contenuto.links);
        },
        error: (err) => console.error('Errore recupero link footer', err),
      });

    this.contenutoStaticoService
      .getByKey(CONTENUTI_DINAMICI_KEYS.FOOTER_ENTI)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (contenuto) => {
          if (contenuto.enti?.length) this.enti = contenuto.enti;
        },
        error: (err) => console.error('Errore recupero enti footer', err),
      });
  }
}
